import { Body, Controller, Delete, Get, Param, Patch, Post, Query, UseGuards } from '@nestjs/common';
import { AuthGuard } from '@nestjs/passport';
import { ApiBearerAuth, ApiOperation, ApiTags } from '@nestjs/swagger';
import { RequireModule } from '../../auth/decorators/require-module.decorator';
import { RequirePermission } from '../../auth/decorators/require-permission.decorator';
import type { LoginUserInfo } from '../../auth/dto/login.dto';
import { GetUser } from '../../auth/get-user.decorator';
import { ModuleGuard } from '../../auth/guards/module.guard';
import { PermissionGuard } from '../../auth/guards/permission.guard';
import {
  CreateParametroDto,
  QueryParametrosDto,
  UpdateParametroDto,
} from '../dto/parametros.dto';
import { ParametrosService } from '../services/parametros.service';

@ApiTags('RRHH - Parámetros del sistema')
@Controller({ path: 'rrhh/parametros', version: '1' })
@UseGuards(AuthGuard('jwt'), ModuleGuard, PermissionGuard)
@RequireModule('RRHH')
@ApiBearerAuth()
export class ParametrosController {
  constructor(private readonly service: ParametrosService) {}

  @Get()
  @RequirePermission('RRHH', 'RH_CFG_PARAMETRO_VER')
  @ApiOperation({ summary: 'Listar parámetros (con filtro opcional ?vigente=true)' })
  listar(@GetUser() user: LoginUserInfo, @Query() query: QueryParametrosDto) {
    return this.service.listar(user.empresa_id, query);
  }

  @Get('vigente/:clave')
  @RequirePermission('RRHH', 'RH_CFG_PARAMETRO_VER')
  @ApiOperation({ summary: 'Obtener parámetro vigente al día de hoy por clave' })
  obtenerVigente(@Param('clave') clave: string, @GetUser() user: LoginUserInfo) {
    return this.service.obtenerVigente(user.empresa_id, clave);
  }

  @Post()
  @RequirePermission('RRHH', 'RH_CFG_PARAMETRO_EDITAR')
  @ApiOperation({ summary: 'Crear parámetro o nueva versión con vigencia' })
  crear(@GetUser() user: LoginUserInfo, @Body() dto: CreateParametroDto) {
    return this.service.crear(user.empresa_id, dto, user.id);
  }

  @Patch(':id')
  @RequirePermission('RRHH', 'RH_CFG_PARAMETRO_EDITAR')
  @ApiOperation({ summary: 'Editar parámetro (sólo si es editable)' })
  actualizar(
    @Param('id') id: string,
    @GetUser() user: LoginUserInfo,
    @Body() dto: UpdateParametroDto,
  ) {
    return this.service.actualizar(id, user.empresa_id, dto, user.id);
  }

  @Delete(':id')
  @RequirePermission('RRHH', 'RH_CFG_PARAMETRO_EDITAR')
  @ApiOperation({ summary: 'Eliminar parámetro (sólo si es editable)' })
  eliminar(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.eliminar(id, user.empresa_id);
  }
}
